class CustomCartError extends Error {
  constructor(message, response = null, payload = null) {
    super(message);
    this.name = 'CustomCartError';
    this.response = response;
    this.payload = payload;
    this.status = response ? response.status : null;
  }
}

class CustomCartAPI {
  constructor(routeConfig = window.routes || {}) {
    this.routes = routeConfig;
    this.queue = Promise.resolve();
    this.cartFetch = null;
    this.rateLimitedUntil = 0;
  }

  enqueue(operation) {
    const queuedOperation = this.queue.then(operation, operation);
    this.queue = queuedOperation.catch(() => undefined);
    return queuedOperation;
  }

  fetchCart() {
    if (this.cartFetch) return this.cartFetch;
    this.cartFetch = this.enqueue(() => this.request(this.cartJsonUrl(), { method: 'GET' }))
      .finally(() => { this.cartFetch = null; });
    return this.cartFetch;
  }

  addItems(items) {
    return this.enqueue(() => this.request(this.routes.cart_add_url, {
      method: 'POST',
      headers: this.jsonHeaders(),
      body: JSON.stringify({ items })
    }));
  }

  addForm(formData) {
    return this.enqueue(() => this.request(this.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    }));
  }

  change(lineKey, quantity) {
    return this.enqueue(() => this.request(this.routes.cart_change_url, {
      method: 'POST',
      retryOnRateLimit: true,
      headers: this.jsonHeaders(),
      body: JSON.stringify({ id: String(lineKey), quantity: Number(quantity) })
    }));
  }

  update(payload) {
    return this.enqueue(() => this.request(this.routes.cart_update_url, {
      method: 'POST',
      retryOnRateLimit: true,
      headers: this.jsonHeaders(),
      body: JSON.stringify(payload)
    }));
  }

  requestSections(sectionIds) {
    const uniqueIds = [...new Set(sectionIds.filter(Boolean))].slice(0, 5);
    const url = new URL(window.location.href);
    url.searchParams.set('sections', uniqueIds.join(','));

    return this.enqueue(() => this.request(`${url.pathname}${url.search}`, { method: 'GET' }));
  }

  cartJsonUrl() {
    const cartUrl = this.routes.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
    return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
  }

  jsonHeaders() {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };
  }

  wait(milliseconds) {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

  retryDelay(response, attempt) {
    const retryAfter = Number(response.headers.get('retry-after'));
    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      return Math.min(5000, Math.max(900, retryAfter * 1000));
    }
    return 1100 * (attempt + 1);
  }

  async request(url, options = {}, attempt = 0) {
    if (!url) throw new CustomCartError('A required Shopify cart route is unavailable.');

    const { retryOnRateLimit = false, ...fetchOptions } = options;
    const method = String(fetchOptions.method || 'GET').toUpperCase();
    const waitBeforeRequest = this.rateLimitedUntil - Date.now();
    if (waitBeforeRequest > 0) await this.wait(waitBeforeRequest);

    let response;
    try {
      response = await fetch(url, { credentials: 'same-origin', ...fetchOptions });
    } catch (error) {
      throw new CustomCartError(error.message || 'The cart request could not be completed.');
    }

    if (response.status === 429) {
      const delay = this.retryDelay(response, attempt);
      this.rateLimitedUntil = Math.max(this.rateLimitedUntil, Date.now() + delay);
      const mayRetry = method === 'GET' || retryOnRateLimit;
      if (mayRetry && attempt < 1) {
        await response.text().catch(() => undefined);
        await this.wait(delay);
        return this.request(url, options, attempt + 1);
      }
    } else {
      this.rateLimitedUntil = 0;
    }

    const contentType = response.headers.get('content-type') || '';
    let payload;

    try {
      const responseText = await response.text();
      const trimmedText = responseText.trim();
      const looksLikeJson = trimmedText.startsWith('{') || trimmedText.startsWith('[');
      payload = contentType.includes('application/json') || looksLikeJson
        ? JSON.parse(responseText)
        : responseText;
    } catch (error) {
      throw new CustomCartError('Shopify returned an unreadable cart response.', response);
    }

    if (!response.ok || (payload && typeof payload === 'object' && payload.status >= 400)) {
      const message = response.status === 429
        ? (window.cartStrings?.error || 'The cart is temporarily busy. Please try again in a moment.')
        : payload?.description || payload?.message || `Cart request failed (${response.status}).`;
      throw new CustomCartError(message, response, payload);
    }

    return payload;
  }
}

window.CustomCartError = CustomCartError;
window.CustomCartAPI = CustomCartAPI;
window.customCartAPI = window.customCartAPI || new CustomCartAPI();
